import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiChevronDown,
  FiHeart,
  FiSearch,
  FiShoppingBag,
  FiUser,
} from "react-icons/fi";

import { logout } from "../../actions/userActions";
import { getCart } from "../../actions/cartActions";

import "./MainHeader.css";

const VALID_ROLES = [
  "user",
  "customer",
  "admin",
  "super_admin",
  "store_manager",
  "editor",
];

const MAIN_NAV_LINKS = [
  { label: "Services", to: "/services" },
  { label: "Made to Measure", to: "/made-to-measure" },
  { label: "Jewellery Hire", to: "/jewelry-hire" },
  { label: "Production", to: "/production" },
  { label: "Bespoke", to: "/bespoke" },
  { label: "Credits", to: "/credits" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const EMPTY_CART_SUMMARY = {
  itemCount: 0,
  distinctItemCount: 0,
  subtotal: 0,
  hasUnavailableItems: false,
  hasPriceChanges: false,
  readyForCheckout: false,
  ui: {
    status: "empty",
    hasItems: false,
    blockingIssueCount: 0,
    unavailableItemCount: 0,
    priceChangedItemCount: 0,
    actions: {
      canCheckout: false,
      canRemoveUnavailableItems: false,
      canRemovePriceChangedItems: false,
    },
  },
};

function getRoles(userInfo) {
  const value = userInfo?.roles ?? userInfo?.role;

  const rawRoles = Array.isArray(value)
    ? value
    : String(value || "")
        .split(",")
        .map((role) => role.trim());

  return [
    ...new Set(
      rawRoles
        .map((role) => String(role).trim().toLowerCase())
        .filter((role) => VALID_ROLES.includes(role)),
    ),
  ];
}

function hasRole(userInfo, targetRole) {
  const role = String(targetRole || "")
    .trim()
    .toLowerCase();

  if (!role) return false;

  return getRoles(userInfo).includes(role);
}

function getFirstName(name) {
  const cleanName = String(name || "").trim();

  if (!cleanName) return "Account";

  return cleanName.split(/\s+/)[0] || "Account";
}

function getSafeCartCount(value) {
  const count = Number(value || 0);

  if (!Number.isFinite(count) || count < 0) return 0;

  return Math.floor(count);
}

export default function MainHeader() {
  const dispatch = useDispatch();
  const location = useLocation();
  const menuId = useId();

  const lastCartFetchKeyRef = useRef("");
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const userAuth = useSelector((state) => state.userAuth || {});
  const cartState = useSelector((state) => state.cart || {});

  const {
    checked = false,
    userInfo = null,
    isAuthenticated = false,
  } = userAuth;

  const {
    loading: cartLoading = false,
    summary = EMPTY_CART_SUMMARY,
    pending = {},
  } = cartState;

  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const userId = userInfo?.id || userInfo?._id || null;

  const isReady = checked;
  const isLoggedIn = Boolean(checked && isAuthenticated && userInfo && userId);
  const isAdmin =
    isLoggedIn &&
    (hasRole(userInfo, "admin") ||
      hasRole(userInfo, "super_admin") ||
      hasRole(userInfo, "store_manager"));

  const firstName = useMemo(() => getFirstName(userInfo?.name), [userInfo]);

  const identityKey = isLoggedIn ? `user:${String(userId)}` : "guest";

  useEffect(() => {
    if (!checked) return;

    if (lastCartFetchKeyRef.current === identityKey) return;

    lastCartFetchKeyRef.current = identityKey;
    dispatch(getCart());
  }, [dispatch, checked, identityKey]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        triggerRef.current?.focus?.();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    if (loggingOut) return;

    try {
      setLoggingOut(true);
      setMenuOpen(false);
      lastCartFetchKeyRef.current = "";
      await dispatch(logout());
    } finally {
      setLoggingOut(false);
    }
  };

  const cartCount = getSafeCartCount(summary?.itemCount);

  const isCartBusy =
    Boolean(cartLoading) || Boolean(pending?.summary || pending?.refresh);

  const cartLabel = isCartBusy
    ? "Cart loading"
    : cartCount > 0
      ? `Cart with ${cartCount} item${cartCount === 1 ? "" : "s"}`
      : "Cart";

  return (
    <header className="academy-topbar">
      <div className="academy-topbar-inner">
        <div className="academy-topbar-left">
          <Link
            to="/"
            className="academy-logo"
            aria-label="Academy Costumes London home"
          >
            <div className="academy-logo-text academy-logo-text--stacked">
              <span
                className="academy-logo-brand"
                role="heading"
                aria-level={1}
              >
                Academy Costumes
              </span>
              <span className="academy-logo-subbrand">London</span>
            </div>
          </Link>
        </div>

        <div className="academy-topbar-right">
          <div className="academy-actions">
            <Link
              to="/search"
              className="academy-action-icon"
              aria-label="Search"
              title="Search"
            >
              <FiSearch aria-hidden="true" />
            </Link>

            <Link
              to="/wishlist"
              className="academy-action-icon"
              aria-label="Wishlist"
              title="Wishlist"
            >
              <FiHeart aria-hidden="true" />
            </Link>

            <Link
              to="/cart"
              className={`academy-action-icon academy-cart-link ${
                isCartBusy ? "is-loading" : ""
              }`}
              aria-label={cartLabel}
              aria-busy={isCartBusy}
              title={cartLabel}
            >
              <FiShoppingBag aria-hidden="true" />

              {cartCount > 0 && (
                <span
                  className="academy-badge"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </Link>

            {!isReady ? (
              <span
                className="academy-action-icon academy-account-pill"
                aria-label="Account loading"
                title="Loading account"
              >
                <FiUser aria-hidden="true" />
              </span>
            ) : isLoggedIn ? (
              <div className="academy-usermenu" ref={menuRef}>
                <button
                  ref={triggerRef}
                  type="button"
                  className="academy-usermenu-trigger academy-account-pill"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-controls={menuOpen ? menuId : undefined}
                  onClick={() => setMenuOpen((prev) => !prev)}
                  disabled={loggingOut}
                >
                  <FiUser aria-hidden="true" />

                  <span className="academy-usermenu-label">{firstName}</span>

                  <FiChevronDown
                    aria-hidden="true"
                    className={`academy-usermenu-chevron ${
                      menuOpen ? "open" : ""
                    }`}
                  />
                </button>

                {menuOpen && (
                  <div
                    id={menuId}
                    className="academy-usermenu-dropdown"
                    role="menu"
                    aria-label="Account menu"
                  >
                    <div className="academy-usermenu-head">
                      <div className="academy-usermenu-name">
                        {userInfo?.name || "Academy Costumes London"}
                      </div>

                      {userInfo?.email ? (
                        <div className="academy-usermenu-email">
                          {userInfo.email}
                        </div>
                      ) : null}
                    </div>

                    <Link
                      to="/profile"
                      className="academy-usermenu-item"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      Profile
                    </Link>

                    <Link
                      to="/orders"
                      className="academy-usermenu-item"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      My Orders
                    </Link>

                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="academy-usermenu-item"
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                      >
                        Admin Panel
                      </Link>
                    )}

                    <button
                      type="button"
                      className="academy-usermenu-item academy-usermenu-button"
                      role="menuitem"
                      onClick={handleLogout}
                      disabled={loggingOut}
                    >
                      {loggingOut ? "Logging out..." : "Logout"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="academy-action-icon academy-account-pill"
                aria-label="Sign in"
              >
                <FiUser aria-hidden="true" />
                <span className="academy-login-copy">Sign in</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="academy-navrow">
        <nav className="academy-mainnav" aria-label="Main navigation">
          {MAIN_NAV_LINKS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              {item.label}
            </NavLink>
          ))}

          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              Admin
            </NavLink>
          )}
        </nav>
      </div>
    </header>
  );
}
